import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  GET_POLICY_BY_POLICY_ID_AND_ENVS,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import { PolicyByIdAndEnvsQuery } from '@agilelab/plugin-wb-governance-common';
import {
  customAlertApiRef,
  GenericList,
  GenericListItem,
  TestStatus,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Tooltip, Typography } from '@material-ui/core';
import { ReactNode, useCallback } from 'react';
import useAsync from 'react-use/lib/useAsync';

const statusIconStyle = {
  marginRight: '8px',
  display: 'flex',
  alignItems: 'center',
};

export function PolicyViolationsOverview({
  title,
  hideIfEmpty = false,
  noDataMessage,
  policyIdFilter,
  resource,
  wrapper,
}: {
  title?: string;
  hideIfEmpty?: boolean;
  noDataMessage?: string;
  policyIdFilter: string[];
  resource: {
    externalId: string;
    environment: string;
  };
  wrapper?: (children: ReactNode) => JSX.Element;
}): JSX.Element {
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();

  const fetchPolicy = useCallback(
    async (policyId: string) => {
      const { data } = await apolloClient.query<PolicyByIdAndEnvsQuery>({
        query: GET_POLICY_BY_POLICY_ID_AND_ENVS,
        variables: { policyId },
      });
      return data.cgp_governance_entity[0];
    },
    [apolloClient],
  );

  const {
    loading,
    value: violationItems,
    error,
  } = useAsync(async () => {
    if (!policyIdFilter.length) return [];
    const { violations } = await governanceApi.getPolicyViolations(
      {
        resource: resource.externalId,
        environment: resource.environment,
        policies: policyIdFilter,
      },
      await identityApi.getCredentials(),
    );

    return Promise.all(
      violations.map(async violation => {
        const policy = await fetchPolicy(violation.policyId);
        return {
          policyName: policy.name,
          policyVersion: `v:${policy.version}`,
          policyDescription: policy.description,
          outcome: violation.outcome.toUpperCase() as DagStatus,
        };
      }),
    );
  }, [governanceApi, identityApi, alertApi, fetchPolicy]);

  const items: GenericListItem[] | undefined = violationItems?.map(v => ({
    icon: (
      <TestStatus
        style={statusIconStyle}
        status={v.outcome}
        filled
        variant="reduced"
      />
    ),
    text: (
      <Tooltip title={v.policyDescription ?? v.policyName}>
        <Typography>{`${v.policyName} ${v.policyVersion}`}</Typography>
      </Tooltip>
    ),
  }));

  if (hideIfEmpty && (loading || (items && !items.length))) return <></>;

  const list = (
    <GenericList
      label={title}
      isLoading={loading}
      isError={!!error}
      error={
        error && (
          <Typography>Policy violations are temporarily unavailable</Typography>
        )
      }
      items={items}
      noData={<Typography>{noDataMessage ?? 'No violations'}</Typography>}
    />
  );

  return wrapper ? wrapper(list) : list;
}
